import React, { useState } from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import loansData from '../../data/loans.json';

const LoanComparison = () => {
  const [loans, setLoans] = useState(loansData);
  const [sortBy, setSortBy] = useState('rating');
  const [filterBy, setFilterBy] = useState('all');

  const handleSort = (criteria) => {
    setSortBy(criteria);
    const sorted = [...loans].sort((a, b) => {
      switch (criteria) {
        case 'rating':
          return b.rating - a.rating;
        case 'interestRate':
          return parseFloat(a.interestRate.split(' - ')[0]) - parseFloat(b.interestRate.split(' - ')[0]);
        case 'loanAmount':
          return parseFloat(b.loanAmount.split(' - ')[1].replace(/[₹,]/g, '')) - parseFloat(a.loanAmount.split(' - ')[1].replace(/[₹,]/g, ''));
        default:
          return 0;
      }
    });
    setLoans(sorted);
  };

  const handleFilter = (filter) => {
    setFilterBy(filter);
    if (filter === 'all') {
      setLoans(loansData);
    } else if (filter === 'lowInterest') {
      setLoans(loansData.filter(loan => parseFloat(loan.interestRate.split(' - ')[0]) <= 12));
    } else if (filter === 'highAmount') {
      setLoans(loansData.filter(loan => parseFloat(loan.loanAmount.split(' - ')[1].replace(/[₹,]/g, '')) >= 2000000));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Compare Loans & Interest Rates"
        description="Compare personal loans, home loans, and interest rates from top banks. Find the best loan options with competitive rates and flexible terms. Loan comparison India."
        keywords="personal loan comparison, home loan comparison, low-interest loans, loan rates, bank loans, loan comparison India, best loans 2025"
        type="website"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Comparisons', url: '/comparisons' },
          { name: 'Loan Comparison', url: '/comparisons/loans' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Loan Comparison', url: '/comparisons/loans' }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Compare Loans & Interest Rates
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Compare personal loans, home loans, and interest rates from top banks. Find the best loan options for your needs.
          </p>
        </div>

        {/* Filters and Sort */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by:</label>
              <select
                value={filterBy}
                onChange={(e) => handleFilter(e.target.value)}
                className="input-field"
              >
                <option value="all">All Loans</option>
                <option value="lowInterest">Low Interest Rate</option>
                <option value="highAmount">High Loan Amount</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => handleSort(e.target.value)}
                className="input-field"
              >
                <option value="rating">Rating</option>
                <option value="interestRate">Interest Rate</option>
                <option value="loanAmount">Loan Amount</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loans.map((loan) => (
            <div key={loan.id} className="card hover:shadow-xl transition-all duration-300">
              {/* Loan Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🏦</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{loan.name}</h3>
                    <p className="text-sm text-gray-600">{loan.bank}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-yellow-400">★</span>
                  <span className="text-sm font-medium text-gray-700">{loan.rating}</span>
                </div>
              </div>

              {/* Key Features */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Interest Rate</span>
                  <span className="font-semibold text-primary-600">{loan.interestRate}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Processing Fee</span>
                  <span className="font-semibold text-gray-900">{loan.processingFee}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Loan Amount</span>
                  <span className="font-semibold text-gray-900">{loan.loanAmount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Tenure</span>
                  <span className="font-semibold text-gray-900">{loan.tenure}</span>
                </div>
              </div>

              {/* Features List */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {loan.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Eligibility */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Eligibility:</h4>
                <p className="text-sm text-gray-600">{loan.eligibility}</p>
              </div>

              {/* Action Button */}
              <button className="w-full btn-primary">
                Apply Now
              </button>
            </div>
          ))}
        </div>

        {/* Educational Content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              💡 Loan Tips
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Compare interest rates from multiple lenders</li>
              <li>• Check processing fees and hidden charges</li>
              <li>• Maintain a good credit score</li>
              <li>• Read terms and conditions carefully</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              📊 Loan Types
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Personal Loans: Unsecured, higher rates</li>
              <li>• Home Loans: Secured, lower rates</li>
              <li>• Car Loans: Secured, moderate rates</li>
              <li>• Education Loans: Special rates for students</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🏆 Best Practices
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Choose the right loan tenure</li>
              <li>• Consider prepayment options</li>
              <li>• Factor in all costs, not just interest</li>
              <li>• Build emergency fund before taking loans</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoanComparison;
